import React, { useCallback, useMemo } from 'react';
import { Button, Modal } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useOrgId, useOrganizations } from 'hooks/index.js';
import { styled } from '@linaria/react';
import { textToHtml } from 'helpers/textToHtml';

const ContactUsModalContent = styled.div`
  white-space: pre-wrap;
`;

const LIVE_RESOURCES_URL = 'https://bambuser.com/docs/live/tags/live/';
const VIDEO_CONSULTATION_RESOURCES_URL = 'https://bambuser.com/docs/video-consultation/tags/video-consultation/';
const CHAT_RESOURCES_URL = 'https://bambuser.com/docs/chat/tags/chat/';
const SHOPPABLE_VIDEOS_RESOURCES_URL = 'https://bambuser.com/docs/shoppable-video/tags/shoppable-videos/';

const ContactUsModalAlt = ({ isModalOpen, closeModal }) => {
  const { t } = useLocalization();
  const orgId = useOrgId();
  const organizations = useOrganizations();
  const orgName = (organizations.data || []).find(org => org.id === orgId)?.title;

  const MODAL_NAME = 'contact-us';
  const mailtoParams = useMemo(() => {
    return `?subject=Support%20Request&body=BamHub%20ID:%20${orgId}%0AProduct%20(select%20one%20or%20more):%20[Live%20/%20Shoppable%20Video%20/%20Video%20Consultation%20/%20Chat]%0AIssue%20Description:%20[Briefly%20describe%20your%20issue%20or%20request]%0ASteps%20to%20Reproduce%20(e.g.,%20Step%201:%20Clicked%20\'Go%20Live\'%20button,%20Step%202:%20Selected%20product,%20Step%203:%20Video%20froze):%20[Describe%20steps]%0AError%20Code/Message%20(if%20applicable):%20[e.g.,%20Error%20403]%0ABrowser/Device:%20[e.g.,%20Chrome%20on%20Desktop%20/%20iOS%20App]%0AApp%20Version%20(if%20applicable):%20[e.g.,%20v2.3.1]%0AImages/Videos:%20[Insert%20URL%20or%20description%20of%20images/videos]%0AWebsite%20URL:%20[Insert%20URL%20for%20website%20or%20other%20attachments]%0A%0ANote:%20Providing%20videos,%20images,%20and%20the%20website%20URL%20greatly%20helps%20us%20reproduce%20your%20issue%20for%20faster%20and%20more%20effective%20support.%0A%0ATroubleshooting%20Resources:%0A-%20Live:%20${LIVE_RESOURCES_URL}%0A-%20Video%20Consultation:%20${VIDEO_CONSULTATION_RESOURCES_URL}%0A-%20Chat:%20${CHAT_RESOURCES_URL}%0A-%20Shoppable%20Video:%20${SHOPPABLE_VIDEOS_RESOURCES_URL}`;
  }, [orgId]);

  const onClickContactSupport = useCallback(() => {
    window.open(`mailto:support@bambuser.com${mailtoParams}`, '_blank');
  }, [mailtoParams]);

  const handleClose = () => closeModal();

  return (
    <Modal
      name={MODAL_NAME}
      isModalOpen={isModalOpen}
      title={t('contactModal.title')}
      onClose={handleClose}
      actions={
        <>
          <Button onClick={handleClose}>{t('contactModal.cancel')}</Button>
          <Button onClick={onClickContactSupport} variant="primary">
            {t('globalHelp.contactLinkText')}
          </Button>
        </>
      }
    >
      <ContactUsModalContent>
        {textToHtml(t('contactModal.altBodyText', {
          supportEmail: 'support@bambuser.com',
          mailtoParams,
          orgName: orgName ? ` (${orgName})` : '',
          orgId: orgId ? ` (${orgId})` : '',
          liveResourcesUrl: LIVE_RESOURCES_URL,
          videoConsultationResourcesUrl: VIDEO_CONSULTATION_RESOURCES_URL,
          chatResourcesUrl: CHAT_RESOURCES_URL,
          shoppableVideoResourcesUrl: SHOPPABLE_VIDEOS_RESOURCES_URL,
        }))}
      </ContactUsModalContent>
    </Modal>
  );
};

export default ContactUsModalAlt;
